import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RebarAuthService implements OnDestroy {
  private readonly destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private auth: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    // MSAL is browser-only — skip subscription during SSR
    if (!isPlatformBrowser(this.platformId)) return;

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe({
        next: () => {
          this.checkAndSetActiveAccount();
          if (this.authenticationEnabled() && !this.IsUserAuthenticated()) {
            this.login();
          }
        },
        error: (err) => console.error('MSAL inProgress$ error', err),
      });
  }

  public IsUserAuthenticated(): boolean {
    return this.auth.instance.getAllAccounts().length > 0;
  }

  public authenticationEnabled(): boolean {
    return environment.providers !== 'mock';
  }

  public checkAndSetActiveAccount(): void {
    const activeAccount = this.auth.instance.getActiveAccount();
    if (!activeAccount && this.auth.instance.getAllAccounts().length > 0) {
      this.auth.instance.setActiveAccount(this.auth.instance.getAllAccounts()[0]);
    }
  }

  /** Session-storage key used to restore the pre-login URL after auth completes. */
  static readonly PRE_LOGIN_URL_KEY = 'ast.redirectAfterLogin';

  public login(): void {
    if (environment.providers !== 'mock') {
      const activeAccount = this.auth.instance.getActiveAccount();
      if (!activeAccount) {
        if (this.auth.instance.getAllAccounts().length > 0) {
          this.auth.instance.setActiveAccount(this.auth.instance.getAllAccounts()[0]);
        } else {
          // Persist the current URL so we can return here after Azure AD redirect.
          sessionStorage.setItem(RebarAuthService.PRE_LOGIN_URL_KEY, window.location.href);
          if (this.msalGuardConfig.authRequest) {
            this.auth.loginRedirect({
              ...this.msalGuardConfig.authRequest,
            } as RedirectRequest);
          } else {
            this.auth.loginRedirect();
          }
        }
      }
    }
  }

  public logout(): void {
    this.auth.logoutRedirect();
  }

  public getUser(): string | undefined {
    return this.auth.instance.getActiveAccount()?.username;
  }

  public getUserName(): string | undefined {
    const account =
      this.auth.instance.getActiveAccount() ?? this.auth.instance.getAllAccounts()[0];
      return account?.username.split("@")[0] ?? account?.name;
  }

  public getDisplayName(): string | undefined {
    const account =
      this.auth.instance.getActiveAccount() ?? this.auth.instance.getAllAccounts()[0];
    return account?.name;
  }

  public async getUserPhotoUrl(): Promise<string | undefined> {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    const account =
      this.auth.instance.getActiveAccount() ?? this.auth.instance.getAllAccounts()[0];
    if (!account) return undefined;
    try {
      const result = await this.auth.instance.acquireTokenSilent({
        account,
        scopes: ['user.read'],
      });
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { Authorization: `Bearer ${result.accessToken}` },
      });
      if (!response.ok) return undefined;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return undefined;
    }
  }

  public getUserClaims(): unknown {
    return this.auth.instance.getAllAccounts()[0]?.idTokenClaims;
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
